import React, { useState } from 'react';
import { AlertTriangle, Calendar, Package, Check, Trash2, History, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAlerts } from '../../hooks/useAlerts';
import { Alerta, AlertasTipo } from '../../types';

const Alerts: React.FC = () => {
  const { alerts, clearedAlerts, loading, error, fetchClearedAlerts, clearAlert, deleteAlert } = useAlerts();
  const [viewMode, setViewMode] = useState<'active' | 'cleared'>('active');
  const [showClearedAlerts, setShowClearedAlerts] = useState(false);

  const handleViewClearedAlerts = async () => {
    if (!showClearedAlerts) {
      await fetchClearedAlerts();
    }
    setShowClearedAlerts(!showClearedAlerts);
    setViewMode(showClearedAlerts ? 'active' : 'cleared');
  };

  const handleClearAlert = async (alertId: string) => {
    if (window.confirm('¿Deseas marcar esta alerta como resuelta?')) {
      await clearAlert(alertId);
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    if (window.confirm('¿Estás seguro de eliminar esta alerta permanentemente?')) {
      await deleteAlert(alertId);
    }
  };

  const getAlertIcon = (tipo: AlertasTipo) => {
    switch (tipo) {
      case AlertasTipo.STOCK_BAJO:
        return AlertTriangle;
      case AlertasTipo.VENCIDO:
        return AlertTriangle;
      case AlertasTipo.PROXIMO_VENCER:
        return Calendar;
      case AlertasTipo.REORDEN:
        return Package;
      default:
        return AlertCircle;
    }
  };

  const getPriorityColor = (prioridad: string) => {
    switch (prioridad) {
      case 'alta': return 'border-red-500 bg-red-50';
      case 'media': return 'border-yellow-500 bg-yellow-50';
      case 'baja': return 'border-blue-500 bg-blue-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const getIconColor = (prioridad: string) => {
    switch (prioridad) {
      case 'alta': return 'text-red-600';
      case 'media': return 'text-yellow-600';
      case 'baja': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const renderAlert = (alert: Alerta, isCleared: boolean = false) => {
    const Icon = getAlertIcon(alert.tipo);
    
    return (
      <div
        key={alert._id}
        className={`bg-white rounded-lg p-6 shadow-sm border-l-4 ${getPriorityColor(alert.prioridad)}`}
      >
        <div className="flex items-start space-x-4">
          <div className={`p-2 rounded-lg bg-white ${getIconColor(alert.prioridad)}`}>
            <Icon size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-dark-text">
                  {alert.productoNombre}
                </h4>
                <p className="text-sm text-medium-gray mt-1">
                  {alert.mensaje}
                </p>
                <p className="text-xs text-light-gray mt-2">
                  Creada: {format(new Date(alert.fechaCreacion), 'dd/MM/yyyy HH:mm', { locale: es })}
                </p>
                {isCleared && alert.fechaResolucion && (
                  <p className="text-xs text-green-600 mt-1">
                    Resuelta: {format(new Date(alert.fechaResolucion), 'dd/MM/yyyy HH:mm', { locale: es })}
                    {alert.resueltoPor && ` por ${alert.resueltoPor}`}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  alert.prioridad === 'alta' ? 'bg-red-100 text-red-800' :
                  alert.prioridad === 'media' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  Prioridad {alert.prioridad}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-4">
              {!isCleared ? (
                <button
                  onClick={() => handleClearAlert(alert._id!)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <Check size={16} />
                  <span>Marcar como resuelta</span>
                </button>
              ) : (
                <button
                  onClick={() => handleDeleteAlert(alert._id!)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <Trash2 size={16} />
                  <span>Eliminar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-medium-gray">Cargando alertas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const displayAlerts = viewMode === 'active' ? alerts : clearedAlerts;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-dark-text font-heading">
          Alertas del Sistema
        </h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-medium-gray">
            {viewMode === 'active' ? (
              <>
                {alerts.length} alerta{alerts.length !== 1 ? 's' : ''} activa{alerts.length !== 1 ? 's' : ''}
              </>
            ) : (
              <>
                {clearedAlerts.length} alerta{clearedAlerts.length !== 1 ? 's' : ''} resuelta{clearedAlerts.length !== 1 ? 's' : ''}
              </>
            )}
          </div>
          <button
            onClick={handleViewClearedAlerts}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'cleared'
                ? 'bg-accent text-cream'
                : 'bg-warm-brown/10 text-warm-brown hover:bg-warm-brown/20'
            }`}
          >
            <History size={18} />
            <span>{viewMode === 'active' ? 'Ver historial' : 'Ver activas'}</span>
          </button>
        </div>
      </div>

      {displayAlerts.length === 0 ? (
        <div className="bg-white rounded-lg p-12 shadow-sm border border-light-gray/20 text-center">
          <div className="text-6xl mb-4">
            {viewMode === 'active' ? '✅' : '📋'}
          </div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">
            {viewMode === 'active' ? '¡Todo en orden!' : 'No hay historial'}
          </h3>
          <p className="text-medium-gray">
            {viewMode === 'active' 
              ? 'No hay alertas activas en este momento'
              : 'No hay alertas resueltas registradas'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayAlerts.map((alert) => renderAlert(alert, viewMode === 'cleared'))}
        </div>
      )}
    </div>
  );
};

export default Alerts;
